import { useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './registration.module.scss';
import ParentAuthPageLayout from '../../../components/auth-page-layout/HsaAuthPageLayout';
import { TextInput, TextInputValue } from '../../../components/text-input/TextInput';
import Button from 'src/components/button/Button';
import { isEmptyObject, validateIsEmpty, validateEmail } from '../../../utils/validation';
import Checkbox from '../../../components/checkbox/Checkbox';
import { useHeaRegisterMutation } from '@src/api/graphql-codegen';
import Spinner from '@components/spinner/Spinner';
import { useQueryParams } from '@hooks/useQueryParams';
import { useAnalytics } from '../../../hooks/useAnalytics';

const emptyErrors = {
  email: '',
  tou: '',
  policy: ''
};

function Registration() {
  const token = useQueryParams().get('token');
  const [tou, setTou] = useState(false);
  const [policy, setPolicy] = useState(false);
  const [email, setEmail] = useState('');
  const [regist, setRegist] = useState(false);
  const [errors, setErrors] = useState(emptyErrors);
  const [serverError, setServerError] = useState('');
  const history = useHistory();
  const { identify, segment } = useAnalytics();

  const [hsaRegister, {loading}] = useHeaRegisterMutation({
    onCompleted(data) {
      if (data.heaRegister) {
        setRegist(true);
        if (data?.heaRegister?.id) {
          segment?.alias(data?.heaRegister?.id);
          segment?.identify(data?.heaRegister?.id, {
            $user_id: data?.heaRegister?.id,
            createdAt: data?.heaRegister?.registeredAt,
          });
          identify(data?.heaRegister?.id, {
            $user_id: data?.heaRegister?.id,
            createdAt: data?.heaRegister?.registeredAt,
          });
        }
      }
    },
    onError(e) {
      setServerError(e.message);
    }
  });

  useEffect(() => {
    if (!token) {
      history.push('/');
    }
  }, [history, token]);

  const handleSubmit = useCallback(() => {

    const errors: any = {};

    if (!tou) {
      errors.tou = 'Please accept the Terms to proceed';
    }

    if (!policy) {
      errors.policy = 'Please accept the Privacy Policy';
    }
    if (validateIsEmpty(email)) {
      errors.email = `Cannot be empty.`;
    }

    if (validateEmail(email)) {
      errors.email = validateEmail(email);
    }

    if (isEmptyObject(errors) && token) {
      setErrors(emptyErrors);
      setServerError('');
      hsaRegister({
        variables: {
          input: {
            token, email
          }
        }
      });
    } else {
      setErrors(errors);
    }
  }, [email, hsaRegister, policy, token, tou]);

  // const handleChangeSelect = useCallback((setter) => (o: SelectOption) => {
  //   setter(o.value);
  // }, []);

  const handleChangeText = useCallback((setter, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    setter(value)
  }, [errors]);

  const handleChangeTou = (v: boolean) => {
    setTou(v);
    setErrors({
      ...errors,
      tou: '',
    });
  };

  const handleChangePolicy = (v: boolean) => {
    setPolicy(v);
    setErrors({
      ...errors,
      policy: '',
    });
  };

  return (
    <ParentAuthPageLayout title={regist ? 'We have sent you an email.' : 'Welcome, please register to continue.'} isRegistration>
      <div className={styles.formContent}>
        {
          regist ? <>
            <div className={styles.subTitle}>Please go to your email and verify your email address.</div>
          </> : <>
          <div className={styles.emailWrapper}>
            <TextInput allowSpecialCharacters hasError={!!errors.email} subTitle={errors.email} value={email}
                      onChange={handleChangeText(setEmail, 'email')} label={'Email:'} id={'email'}
                      placeholder={'Email'}/>
          </div>
          <div className={styles.touWrapper}>
            <Checkbox id={'tou'} checked={tou} onChange={handleChangeTou}>
              <span className={styles.touText}>I have read and agree to the <a className={styles.touLink} onClick={() => {window.open(
                "https://openparachuteschools.com/terms-of-use", "_blank")}} target="_blank" rel="noreferrer" href="https://openparachuteschools.com/terms-of-use">Terms of Use</a></span>
            </Checkbox>
            {!!errors.tou && <div className={styles.serverError}>{errors.tou}</div>}
          </div>
          <div className={styles.touWrapper}>
            <Checkbox id={'policy'} checked={policy} onChange={handleChangePolicy}>
              <span className={styles.touText}>I have read and agree to the <a className={styles.touLink} onClick={() => {window.open(
                "https://openparachuteschools.com/privacy-policy/", "_blank")}} target="_blank" rel="noreferrer" href="https://openparachuteschools.com/privacy-policy/">Privacy Policy</a></span>
            </Checkbox>
            {!!errors.policy && <div className={styles.serverError}>{errors.policy}</div>}
          </div>
          {Boolean(serverError) && <div className={styles.serverError}>{serverError}</div>}
          <Button disabled={loading} size="small" className={styles.submit} onClick={handleSubmit} label={'Register'} leftIcon={loading ? <Spinner width={20} height={20} /> : undefined}/>
          <div className={styles.clickText}>Have registered already? <a className={styles.clickLink} href="/login">Click Here</a></div>
        </>}
      </div>
    </ParentAuthPageLayout>
  )
};

export default Registration;
